import React, {Fragment, useEffect, useState} from 'react';
import {Modal, Button, Input, Checkbox, Loader, Dropdown} from 'semantic-ui-react';
import {apiGet, apiPost} from '../../../services/apiClient';
import {toast} from 'react-toastify';
import ConfirmModal from '../../../modals/confirmModal';
import Info from '../../../components/Info';
import './costCentersManageModal.scss';

const EMPTY_SUB_COMPANY_VALUE = '';

const CostCentersManageModal = ({open, companyId, onClose}) => {
    const [name, setName] = useState('');
    const [filter, setFilter] = useState('');
    const [subCompanyId, setSubCompanyId] = useState(EMPTY_SUB_COMPANY_VALUE);
    const [subCompanies, setSubCompanies] = useState([]);
    const [costCenters, setCostCenters] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [loading, setLoading] = useState(false);
    const confirmModalRef = React.useRef(null);

    const loadSubCompanies = () => {
        if (!companyId) return Promise.resolve();
        return apiGet(`ref/subCompanies/list/${companyId}`)
            .then(data => {
                const entries = (data && data.entries) || [];
                setSubCompanies(entries);
                if (entries.length === 1) {
                    setSubCompanyId(String(entries[0].id));
                }
            })
            .catch(err => toast.error((err && (err.error || err.Error)) || 'Не удалось загрузить секторы'));
    };

    const loadCostCenters = () => {
        if (!companyId) return Promise.resolve();
        setLoading(true);
        return apiPost('ref/costcenters/company/list', {companyId})
            .then(data => setCostCenters((data && data.entries) || []))
            .catch(err => toast.error((err && (err.error || err.Error)) || 'Не удалось загрузить список'))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (open && companyId) {
            setName('');
            setFilter('');
            setSubCompanyId(EMPTY_SUB_COMPANY_VALUE);
            setSelectedIds([]);
            setLoading(true);
            Promise.all([loadSubCompanies(), loadCostCenters()])
                .finally(() => setLoading(false));
        }
    }, [open, companyId]);

    const getSubCompanyOptions = () => {
        const options = [
            {value: EMPTY_SUB_COMPANY_VALUE, text: 'Без сектора', key: 'sub-company-empty'}
        ];
        subCompanies.forEach(entry => {
            options.push({value: String(entry.id), text: entry.name, key: `sub-company-${entry.id}`});
        });
        return options;
    };

    const getFilteredCostCenters = () => {
        const query = (filter || '').trim().toLowerCase();
        if (!query) {
            return costCenters;
        }
        return costCenters.filter(c => {
            const label = [c.name, c.subCompanyName].filter(Boolean).join(' ').toLowerCase();
            return label.indexOf(query) >= 0;
        });
    };

    const getCostCenterLabel = (costCenter) => {
        if (costCenter.subCompanyName) {
            return `${costCenter.name} (${costCenter.subCompanyName})`;
        }
        return costCenter.name;
    };

    const toggleSelect = (id) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : prev.concat(id));
    };

    const addCostCenter = () => {
        if (!name.trim()) return;
        const payload = {
            companyId,
            name: name.trim(),
            subCompanyId: subCompanyId ? parseInt(subCompanyId, 10) : null
        };
        setLoading(true);
        apiPost('ref/costcenters/manage', payload)
            .then(() => {
                setName('');
                toast.success('Кост-центр добавлен');
                return loadCostCenters();
            })
            .catch(err => toast.error((err && (err.error || err.Error)) || 'Не удалось добавить'))
            .finally(() => setLoading(false));
    };

    const deleteSelected = () => {
        if (!selectedIds.length || !confirmModalRef.current) return;
        confirmModalRef.current.open('Удалить запись(и)?', 'Выбранные кост-центры будут удалены из справочника.', 'Удалить', 'Отмена')
            .then(() => {
                setLoading(true);
                return apiPost('ref/costcenters/delete', {companyId, ids: selectedIds});
            })
            .then(() => {
                setSelectedIds([]);
                toast.success('Записи удалены');
                return loadCostCenters();
            })
            .catch(err => {
                if (err) {
                    toast.error((err && (err.error || err.Error)) || 'Не удалось удалить');
                }
            })
            .finally(() => setLoading(false));
    };

    const renderList = () => {
        const filtered = getFilteredCostCenters();
        if (loading && !costCenters.length) {
            return <div className="cost-centers-manage-modal__loading"><Loader active inline size="small" /> Загрузка...</div>;
        }
        if (!costCenters.length) {
            return <div className="cost-centers-manage-modal__empty">Нет записей. Добавьте кост-центр выше.</div>;
        }
        if (!filtered.length) {
            return <div className="cost-centers-manage-modal__empty">Ничего не найдено по фильтру.</div>;
        }
        return filtered.map(c => {
            const selected = selectedIds.includes(c.id);
            return (
                <div
                    key={c.id}
                    className={'cost-centers-manage-modal__item' + (selected ? ' cost-centers-manage-modal__item--selected' : '')}
                >
                    <Checkbox
                        label={getCostCenterLabel(c)}
                        checked={selected}
                        onChange={() => toggleSelect(c.id)}
                    />
                </div>
            );
        });
    };

    return (
        <Fragment>
            <Modal open={open} onClose={onClose} size="small" className="cost-centers-manage-modal">
                <Modal.Header>Добавить/удалить кост-центр</Modal.Header>
                <Modal.Content>
                    <div className="cost-centers-manage-modal__notice">
                        <Info text="Добавить или удалить кост-центр из справочника юр. лица. Уже оформленные посылки не изменятся." />
                        <span>Справочник кост-центров для выбранного юр. лица</span>
                    </div>

                    <div className="cost-centers-manage-modal__section">
                        <div className="cost-centers-manage-modal__section-title">Новый кост-центр</div>
                        <div className="cost-centers-manage-modal__label-row">
                            <span>Сектор</span>
                            <Info text="Сектор, к которому относится кост-центр. «Без сектора» — доступен для всех секторов юр. лица." />
                        </div>
                        <Dropdown
                            fluid
                            selection
                            options={getSubCompanyOptions()}
                            value={subCompanyId}
                            onChange={(e, {value}) => setSubCompanyId(value)}
                            disabled={loading}
                            className="cost-centers-manage-modal__dropdown"
                        />
                        <div className="cost-centers-manage-modal__label-row cost-centers-manage-modal__label-row--spaced">
                            <span>Название</span>
                        </div>
                        <Input
                            fluid
                            value={name}
                            onChange={(e, {value}) => setName(value)}
                            placeholder="Введите название кост-центра"
                            disabled={loading}
                        />
                    </div>

                    <div className="cost-centers-manage-modal__section cost-centers-manage-modal__section--list">
                        <div className="cost-centers-manage-modal__section-title">
                            Список кост-центров
                            {costCenters.length > 0 && (
                                <span style={{fontWeight: 400, color: '#6b7c93', marginLeft: 8}}>
                                    ({getFilteredCostCenters().length}{filter.trim() ? ' из ' + costCenters.length : ''})
                                </span>
                            )}
                        </div>
                        {costCenters.length > 0 && (
                            <Input
                                fluid
                                icon="search"
                                iconPosition="left"
                                value={filter}
                                onChange={(e, {value}) => setFilter(value)}
                                placeholder="Фильтр по названию или сектору"
                                disabled={loading}
                                className="cost-centers-manage-modal__filter"
                            />
                        )}
                        <div className="cost-centers-manage-modal__list">
                            {renderList()}
                        </div>
                    </div>
                </Modal.Content>
                <Modal.Actions>
                    <div className="cost-centers-manage-modal__actions-row">
                        <Button onClick={onClose} disabled={loading}>Закрыть</Button>
                        <Button
                            negative
                            disabled={!selectedIds.length || loading}
                            onClick={deleteSelected}
                        >
                            Удалить{selectedIds.length > 0 ? ' (' + selectedIds.length + ')' : ''}
                        </Button>
                        <Button primary disabled={!name.trim() || loading} loading={loading} onClick={addCostCenter}>
                            Добавить
                        </Button>
                    </div>
                </Modal.Actions>
            </Modal>
            <ConfirmModal ref={confirmModalRef} />
        </Fragment>
    );
};

export default CostCentersManageModal;



// WEBPACK FOOTER //
// ./App/pages/admin/companies/costCentersManageModal.jsx